'use client';
import { createContext, useContext } from 'react';
import { ContactMethod, SortType } from '../../../types/car-insurance';
import { LeadData } from '../../../types/zohoLead';
import { requireNonNull } from '../../../utils/requireNonNull';

export type Driver = {
  age: number;
  dateOfBirth: string;
  gender: string;
  maritalStatus: string;
  numberOfClaims: number;
  occupationType?: string;
  totalDrivingExperience: number;
};

export type Vehicle = {
  makeName: string;
  modelCode: string;
  modelName: string;
  manufactureYear: number | string;
  trim: string;
  fairMarket: number;
  type: string;
  enquiryPurpose?: string;
  noInsurance?: boolean;
  insuranceExpiryDate?: string;
  consentDirectMarketing?: boolean;
};

export type Prices = {
  propertyDamage: number;
  personalAccident: number;
  isActsOfNature: boolean;
};

export type User = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  preferredContactMethod?: ContactMethod;
};

type Field<T> = Partial<{
  [key in keyof T]: T[key];
}>;

export interface CarInsuranceLeadData extends LeadData {
  Vehicle_Type?: string;
  Car_Make?: string;
  Car_Model?: string;
  Car_Year?: string;
  Car_Variant?: string;
  Bodily_Injury?: string;
  Property_Damage?: string;
  Acts_of_Nature_Coverage?: string | null;
  Personal_Accident?: string;
  FMV?: string;
}

export type CarInsuranceContextState = {
  options: {
    shouldOverride: { value: boolean; setValue: (value: boolean) => void };
  };
  form: {
    isChanged: boolean;
    setIsChanged: (val: boolean) => void;
  };
  requestParams: {
    driver: {
      value: Driver;
      setValue: (value: Field<Omit<Driver, 'dateOfBirth'>>) => void;
    };
    vehicle: {
      value: Vehicle;
      setValue: (updates: Field<Vehicle>) => void;
    };
    ncd: {
      value: number;
      setValue: (value: number) => void;
    };
    offenceFree: {
      value: string;
      setValue: (value: string) => void;
    };
    offPeakVehicle: {
      value: string;
      setValue: (value: string) => void;
    };
    prices: { value: Prices; setValue: (updates: Field<Prices>) => void };
    user: { value: User; setValue: (updates: User) => void };
  };
  filters: {
    providers: {
      value: string[];
      setValue: (value: string[]) => void;
      toggleProvider: (value: string) => void;
    };
    sort: {
      value: SortType;
      setValue: (value: SortType) => void;
    };
  };
  sidebars: {
    editQuoteSidebar: {
      isOpen: boolean;
      setIsOpen: (value: boolean) => void;
    };
    providersSidebar: {
      isOpen: boolean;
      setIsOpen: (value: boolean) => void;
    };
    sortSidebar: {
      isOpen: boolean;
      setIsOpen: (value: boolean) => void;
    };
  };
  dialogs: {
    quoteDetailsDialog: {
      isOpen: boolean;
      setIsOpen: (value: boolean) => void;
    };
  };
  data: {
    vehicleMakers: {
      value: { value: string; label: string }[];
      setValue: (value: { value: string; label: string }[]) => void;
    };
    vehicleModels: {
      value: { value: string; label: string }[];
      setValue: (value: { value: string; label: string }[]) => void;
    };
    vehicleTrims: {
      value: { value: string; label: string }[];
    };
    yearsOptions: { value: { value: string; label: string }[] };
    isVehiclesDataLoading: boolean;
    isYearsLoading: boolean;
    isTrimsLoading: boolean;
  };
  useBolttechData?: boolean;
  sendLeadToZoho: (
    leadData: CarInsuranceLeadData,
  ) => Promise<{ leadId?: string; success?: boolean }>;
  getCarInsuranceLayoutId: () => Promise<string>;
};

export const CarInsuranceContext =
  createContext<CarInsuranceContextState | null>(null);

export function useCarInsuranceContext(): CarInsuranceContextState {
  return requireNonNull(
    useContext(CarInsuranceContext),
    'Car Insurance context is not set',
  );
}
